/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import WithStripe from 'components/common/WithStripe';
import { Field, Form, Formik } from 'formik';
import { connect, useDispatch, batch } from 'react-redux';
import * as yup from 'yup';
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Star,
  Utensils,
  X,
} from 'lucide-react';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { SERVING_STYLE } from 'consts/checkout';
import {
  REQUIRED_VALIDATION,
  REQUIRED_SIGNIN_FOR_PAYMENT,
  INVALID_EMAIL,
  INVALID_INTEGER,
  DEFAULT_TIME_FORMAT,
  DEFAULT_DATE_FORMAT_SAFARI,
} from 'consts/form';
import FormError from 'components/common/FormError';
import DatePickerField from 'components/common/DatePickerField';
import AddressPickerField from 'components/common/AddressPickerField';
import {
  buildCheckoutDataFromPresetItem,
  buildPaymentIntentPresetItems,
} from 'utils/preset.util';
import { useNavigate } from 'react-router-dom';
import {
  useHistory,
  useLocation,
  useParams,
  withRouter,
} from 'react-router-dom';
import LoadingAnimation from 'components/common/LoadingAnimation';
import { setInitialValues } from 'components/common/DeliveryInfoForm';
import { showSignInModal } from 'redux/actions/modalActions';
import {
  addLastMinuteItemPayment,
  cateringOrder,
  cateringOrderUpdate,
  updatePaymentMethodForcateringOrderFinalize,
  getCateringOrder,
  getLastMinuteAddItem,
  clearCateringOrder,
} from 'redux/catering/actions';
import { getPresetDetail } from 'api/presetApi';
import { format, isEqual, parse } from 'date-fns';
import { zonedTimeToUtc } from "date-fns-tz";
import PresetItemView from 'components/common/PresetItemView';
import { store } from 'react-notifications-component';
import { resetCheckoutData, setPreset } from 'redux/restaurants/actions';
import OrderDetails from 'components/common/OrderDetails';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import { PRESET_TRUNCATE_THRESHOLD } from 'consts/presets';
import TruncateWrapper from 'components/common/TruncateWrapper';
import RestaurantMenu from 'pages/RestaurantDetails/RestaurantMenu';
import OrderBasket from 'components/common/OrderBasket';
import { createDeliveryFees, createLastMinuteAddItem } from 'api/cateringApi';
import StripeFormModal from 'pages/OrderEdit/StripeFormModal';
import UpdatePaymentModal from './UpdatePaymentModal';
import EftFormModal from 'pages/Checkout/EftFormModal';
import {
  LOADING_CATERINGORDER_FALSE,
  LOADING_CATERINGORDER_TRUE,
} from 'redux/catering/actionTypes';
import { removeUserCart } from 'redux/userCart/actions';
import './order.scss';
import { validateStoreHours } from 'utils/validateStoreHours.util';
import { authenticateWithToken } from 'api/authApi';
import { signinRequest } from 'redux/actions/authActions';
const queryString = require('query-string');

const validationSchema = yup.object().shape({
  address: yup.string().required(REQUIRED_VALIDATION),
  date: yup
    .date()
    .typeError(REQUIRED_VALIDATION)
    .required(REQUIRED_VALIDATION)
    .notOneOf([null], REQUIRED_VALIDATION),
  time: yup
    .date()
    .typeError(REQUIRED_VALIDATION)
    .required(REQUIRED_VALIDATION)
    .notOneOf([null], REQUIRED_VALIDATION),
  notes: yup.string(),
  servingStyle: yup
    .mixed()
    .oneOf([SERVING_STYLE.INDIVIDUAL, SERVING_STYLE.FAMILY]),
  cutleryNeeded: yup.boolean(),
  cutleryCount: yup
    .number()
    .min(0, INVALID_INTEGER)
    .nullable()
    .typeError(INVALID_INTEGER),
  firstName: yup.string().required(REQUIRED_VALIDATION),
  lastName: yup.string().required(REQUIRED_VALIDATION),
  companyName: yup.string().required(REQUIRED_VALIDATION),
  phoneNumber: yup.string().required(REQUIRED_VALIDATION),
  emailAddress: yup
    .string()
    .typeError(INVALID_EMAIL)
    .email(INVALID_EMAIL)
    .required(REQUIRED_VALIDATION),
});

// A Tailwind-styled stand-in for the shared <ConfirmModal> (which stays on
// react-bootstrap's <Modal> for the many other, still-old-design pages that
// use it - see src/components/common/ConfirmModal.js). Scoped to this file
// only so the Edit Order page's popups match the new "panda" design system
// (same overlay/panel treatment as panda-ui/components/ItemModal) without
// touching that shared component or any page that still relies on it.
const ConfirmDialog = ({
  description,
  confirmText = 'Yes',
  cancelText = 'Cancel',
  onConfirm,
  onClose,
}) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-[#202124]/70 backdrop-blur-xs p-4"
    onClick={onClose}
  >
    <div
      className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full border border-panda-tint p-6 sm:p-7"
      onClick={e => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-3 w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-[#E53935] hover:text-white flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <p className="font-heading font-bold text-base sm:text-lg text-[#202124] pr-6 leading-snug">
        {description}
      </p>
      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-heading font-bold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {cancelText}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark text-white text-sm font-heading font-bold shadow-md shadow-panda-red/30 hover:from-panda-dark hover:to-panda-dark transition-all active:scale-98"
        >
          {confirmText}
        </button>
      </div>
    </div>
  </div>
);

function OrderEdit({
  user,
  getCateringOrder,
  getLastMinuteAddItem,
  createLastMinuteAddItem,
  cateringOrderInfo,
  orderItem,
  cateringOrder,
  setPreset,
  cateringOrderUpdate,
  updatePaymentMethodForcateringOrderFinalize,
  cateringOrderError,
  clearCateringOrder,
}) {
  const dispatch = useDispatch();
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const deliveryInfoDefaultValues = setInitialValues();
  const [addressForCoordinates, setAddressForCoordinates] = useState(null);
  const [preset, setPresetLocal] = useState({});
  const [checkoutData, setCheckoutData] = useState([]);
  const [initialCheckout, setInitialCheckout] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [restaurantAddress, setRestaurantAddress] = useState(null);
  const [addressPickerFocused, setAddressPickerFocused] = useState(false);
  const [datePickerFocused, setDatePickerFocused] = useState(false);
  const [timePickerFocused, setTimePickerFocused] = useState(false);
  const [currentPresetItem, setCurrentPresetItem] = useState(null);
  const [removePresetId, setRemovePresetId] = useState(null);
  const [updateChanges, setUpdateChanges] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [deliveryData, setDeliveryData] = useState(null);
  const { deliveryFee, ranges } = deliveryData || { deliveryFee: 0 };
  const [addItemChanges, setAddItemChanges] = useState(false);
  const [cancelChanges, setCancelChanges] = useState(false);
  const [currentModel, setCurrentModel] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const [stripeErrorMessage, setStripeErrorMessage] = useState('');

  const [initialValues, setInitialValuesLocal] = useState({
    servingStyle: SERVING_STYLE.INDIVIDUAL,
    address: deliveryInfoDefaultValues.address,
    address2: '',
    date: deliveryInfoDefaultValues.date,
    time: deliveryInfoDefaultValues.time,
    notes: '',
    cutleryNeeded: false,
    cutleryCount: 0,
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    emailAddress: '',
  });
  const [changes, setChanges] = useState(false);
  const [loading, setLoadingLocal] = useState(false);
  const { order_id: orderId } = useParams(); // I guess will be used later to fetch real Chef data
  const { pathname, search } = useLocation();
  const history = useHistory();
  const chefSlug = preset?.slug;
  const isSignedIn = user && user.confirmed;
  const AddExtraItem = cateringOrderInfo?.isAddonOrdersEnabled;
  const standardCutleryCharges = 0.5; //dollars;
  const standardCutleryChargesInCents = 50; //cents;
  const { token } = queryString.parse(search);
  // const AddExtraItem = isAddonOrdersEnabled; //is_addon_orders_enabled; isAddonOrdersEnabled
  async function fetchUserData(token) {
    try {
      const auth = await authenticateWithToken({
        Authorization: `Bearer ${token}`,
      });
      if (auth && auth.user && auth.user.confirmed) {
        dispatch(signinRequest(auth));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
        if (orderId) {
          setLoading(true);
          getCateringOrder(+orderId);
        }
      }
    } catch (e) {
      console.log('error', e);
    }
  }
  useEffect(() => {
    if (token) {
      fetchUserData(token);
    }
  }, [token, orderId]);
  useEffect(() => {
    if (!isSignedIn && !token) {
      history.push({
        pathname: '/',
        state: {
          showSignIn: true,
          linkRedirect: pathname,
        },
      });
    }
  });

  useEffect(() => {
    if (cateringOrderInfo?.presetId) {
      const fetchData = async () => {
        const preset = await getPresetDetail(cateringOrderInfo?.presetId);
        setPresetLocal(preset);
        setLoading(false);
      };
      fetchData();

      const dataTime =
        cateringOrderInfo?.orderDateTime &&
        new Date(cateringOrderInfo.orderDateTime);

      const displayTime = (() => {
        const timePart = cateringOrderInfo?.displayDeliveryDateTime?.split(
          ' at ',
        )[1];
        if (!timePart) return dataTime;

        let [hours, minutes] = timePart.split(/[: ]/);
        const modifier = timePart.slice(-2);

        hours = parseInt(hours, 10);
        if (modifier === 'PM' && hours !== 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;

        const time24 = `${String(hours).padStart(2, '0')}:${minutes}:00`;
        return new Date(`1970-01-01T${time24}`);
      })();

      setInitialValuesLocal(prev => ({
        ...prev,
        servingStyle:
          cateringOrderInfo?.extraInfo?.servingStyle || prev.servingStyle,
        address: cateringOrderInfo?.deliveryAddress || prev.address,
        address2: cateringOrderInfo?.deliverySuiteAddress || '',
        date: dataTime || prev.date,
        time: displayTime || prev.time,
        notes: cateringOrderInfo?.moreDetail || '',
        cutleryNeeded: !!cateringOrderInfo?.extraInfo?.numCutlery || false,
        cutleryCount: cateringOrderInfo?.extraInfo?.numCutlery || 0,
        firstName: cateringOrderInfo?.firstName || '',
        lastName: cateringOrderInfo?.lastName || '',
        companyName: cateringOrderInfo?.companyName || '',
        phoneNumber: cateringOrderInfo?.phoneNumber || '',
        emailAddress: cateringOrderInfo?.email || '',
      }));
      setAddressForCoordinates(cateringOrderInfo?.deliveryAddress);
    }
  }, [cateringOrderInfo?.presetId]);

  useEffect(() => {
    if (cateringOrderError && cateringOrderError !== 'Not authorized') {
      history.push('/error');
    }
  }, [cateringOrderError]);

  useEffect(() => {
    if (preset?.id && cateringOrderInfo?.presetId) {
      const { address1, address2, city, state, zip, country } = preset.chefs[0];
      const newArr = [];
      setRestaurantAddress(
        [address1, address2, city, state, zip, country]
          .filter(address => address)
          .join(', '),
      );

      // eslint-disable-next-line no-unused-expressions
      cateringOrderInfo?.purchaseItems?.forEach(item => {
        const repArr = newArr.findIndex(
          item2 => item2.menuId === item.parentId,
        );
        const menu = preset?.chefs?.[0]?.menus?.find(
          item2 => +item2.id === +item.parentId,
        );
        const menuItems = menu?.menuItems;
        const menuItemsMapping = {};
        (menuItems || []).forEach(item2 => {
          menuItemsMapping[item2.id] =
            item.modelId === item2.id
              ? item.amount
              : newArr?.[repArr]?.menuItemsMapping?.[item2.id] || 0;
        });
        if (repArr > -1) {
          newArr[repArr] = {
            ...newArr[repArr],
            menuItemsMapping,
            quantity: +newArr[repArr].quantity + +item.amount,
          };

          const existingServingIndex = newArr[
            repArr
          ]?.servingDetails?.findIndex(sd => sd.id === item.modelId);

          if (existingServingIndex > -1) {
            newArr[repArr].servingDetails[existingServingIndex].items.push(
              ...item.servingDetails,
            );
          } else if (item.servingDetails.length) {
            newArr[repArr].servingDetails.push({
              id: item.modelId,
              items: item.servingDetails,
            });
          }
        } else {
          newArr.push({
            ...menu,
            chefId: cateringOrderInfo?.chefId,
            id: `Additional_${item.parentId}`,
            menuId: item.parentId,
            menuItems,
            servingDetails: item.servingDetails.length
              ? [
                {
                  id: item.modelId,
                  items: item.servingDetails,
                },
              ]
              : [],
            menuItemsMapping,
            name: item.modelType === 'MenuItem' ? item.menuName : item.name,
            presetMenuCategoryName: 'Additional Items',
            priceCents: item.itemPrice,
            quantity: item.amount,
            specialInstruction: item.detail,
          });
        }
      });
      setInitialCheckout(newArr);
      setCheckoutData(newArr);
    }
  }, [preset]);
  const setCurrentPresetItemFromCheckoutData = presetItemId => {
    const presetItem = checkoutData.find(
      presetItem => presetItem.id === presetItemId,
    );
    if (presetItem) {
      setCurrentPresetItem(presetItem);
    }
  };

  const removePresetItem = removeItemId => {
    const filteredCheckoutData = checkoutData.filter(
      presetItemData => presetItemData.id !== removeItemId,
    );

    const { presetItems } = preset;
    const filteredPresetItems = presetItems.filter(
      presetItem => presetItem.id !== removeItemId,
    );

    setCheckoutData(filteredCheckoutData);
    setPreset({ ...preset, presetItems: filteredPresetItems });

    setChanges(true);

    store.addNotification({
      title: 'Item removed!',
      message: 'If you want to add it back, please check additional items.',
      type: 'danger',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animated', 'bounceIn'],
      animationOut: ['animated', 'fadeOut'],
      dismiss: {
        duration: 4000,
      },
    });
  };

  const updateCheckoutData = (newPresetItem, newPresetItemData) => {
    const newCheckoutData = checkoutData.map(item => {
      if (item.id === newPresetItem.id) {
        return { ...newPresetItem, ...newPresetItemData };
      }

      return item;
    });
    setCheckoutData(newCheckoutData);
    setPreset({
      ...preset,
      presetItems: newCheckoutData,
    });

    const foundItem = checkoutData.find(item => item.id === newPresetItem.id);

    if (!foundItem) {
      setPreset({
        ...preset,
        presetItems: [
          ...checkoutData,
          { ...newPresetItem, ...newPresetItemData },
        ],
      });
      setCheckoutData(prev => [
        ...prev,
        { ...newPresetItem, ...newPresetItemData },
      ]);

      store.addNotification({
        title: 'Item added!',
        message: 'Please check the additional items category.',
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: {
          duration: 4000,
        },
      });
    }
  };

  const getCurrentPresetItemChefInfo = useMemo(() => {
    if (!(currentPresetItem && preset?.chefs?.length)) {
      return null;
    }

    const currentChef = preset?.chefs?.find(
      chef => chef.id === currentPresetItem.chefId,
    );

    const menu = currentChef?.menus?.find(
      menu => menu.id === currentPresetItem.menuId,
    );

    return {
      chefImage: currentChef.profileImageUrl,
      menuType: menu.menuType,
      minOrderAmount: menu.minOrderAmount,
    };
  }, [currentPresetItem]);
  useEffect(() => {
    if (isSignedIn && addressForCoordinates && cateringOrderInfo?.chefId) {
      setAddressLoading(true);
      geocodeByAddress(addressForCoordinates)
        .then(results =>
          getLatLng(results[0]).then(async prop => {
            const body = {
              chef_id: cateringOrderInfo?.chefId,
              user_lat: prop.lat,
              user_lng: prop.lng,
              address: addressForCoordinates,
            };
            const response = await createDeliveryFees(body);
            setDeliveryData(response);
            setAddressLoading(false);
          }),
        )
        .catch(error => {
          setAddressLoading(false);
          console.error('Error', error);
        });
    }
  }, [addressForCoordinates, cateringOrderInfo?.chefId, isSignedIn]);
  useEffect(() => {
    if (orderId && !token) {
      setLoading(true);
      getCateringOrder(+orderId);
    }
  }, [orderId]);

  const handleUpdate = () => setUpdateChanges(true);
  const handleCancel = () => setCancelChanges(true);

  const updateOrder = async (checkoutData, preset, form, executeCompletion) => {
    try {
      // Check if there are no items in the order
      if (!checkoutData || checkoutData.length === 0) {
        store.addNotification({
          title: 'No Items',
          message: 'At least one item is required in your order.',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: {
            duration: 4000,
          },
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      const {
        firstName,
        lastName,
        companyName,
        phoneNumber,
        emailAddress,
        cutleryNeeded,
        cutleryCount,
        servingStyle,
      } = form;
      const cart = {
        presetId: preset?.id,
        presetName: preset?.name,
        numCutlery: cutleryNeeded ? parseInt(cutleryCount, 10) : 0,
        firstName,
        lastName,
        companyName,
        phoneNumber,
        email: emailAddress,
        servingStyle,
        presetItems: buildPaymentIntentPresetItems(checkoutData),
        receivingMethod: cateringOrderInfo?.orderType,
      };
      const dateTime =
        cateringOrderInfo?.orderDateTime &&
        new Date(cateringOrderInfo.orderDateTime);
      // const dataAndTime =
      //   form &&
      //   form.date &&
      //   form.time &&
      //   (!isEqual(dateTime, form.date) || !isEqual(dateTime, form.time))
      //     ? new Date(
      //         `${format(form.date, DEFAULT_DATE_FORMAT_SAFARI)} ${format(
      //           form.time,
      //           DEFAULT_TIME_FORMAT,
      //         )} ${preset?.presetLocation?.currentTime?.slice(-6)}`,
      //       )
      //     : '';
      const timezone = preset?.presetLocation?.cityTimeZoneName || "America/Toronto";

      const dataAndTime =
        form &&
          form.date &&
          form.time &&
          (!isEqual(dateTime, form.date) || !isEqual(dateTime, form.time))
          ? (() => {
            const localString = `${format(form.date, "yyyy-MM-dd")} ${format(
              form.time,
              "HH:mm"
            )}`;
            const localDate = parse(localString, "yyyy-MM-dd HH:mm", new Date());
            return zonedTimeToUtc(localDate, timezone);
          })() : "";
      if (cateringOrderInfo?.orderType === 'delivery') {
        cart.deliveryAddress = form ? form.address : '';
        cart.deliverySuiteAddress = form ? form.address2 : '';
        dataAndTime && (cart.deliveryDate = dataAndTime);
        dataAndTime && (cart.deliveryTime = dataAndTime);
        cart.deliveryInstruction = form ? form.notes : '';
        cart.deliveryFee = deliveryFee;
      } else {
        dataAndTime && (cart.pickupDate = dataAndTime);
        dataAndTime && (cart.pickupTime = dataAndTime);
        cart.pickupInstruction = form ? form.notes : '';
      }

      // eslint-disable-next-line react/prop-types
      const isEft = cateringOrderInfo?.purchasedWith === 'acss_debit';

      await cateringOrderUpdate(cart, orderId, isEft);
      if (executeCompletion) {
        await history.push('/checkout-completed');
      }
    } catch (e) {
      // alert(e.message)
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async form => {
    if (!isSignedIn) {
      showSignInModal(REQUIRED_SIGNIN_FOR_PAYMENT);
      return;
    }
    const executeCompletion = true;
    await updateOrder(checkoutData, preset, form, executeCompletion);
  };
  const onSubmitUpdatePayment = async ({ type, values }) => {
    if (!isSignedIn) {
      showSignInModal(REQUIRED_SIGNIN_FOR_PAYMENT);
      return;
    }
    const executeCompletion = false;
    await updateOrder(checkoutData, preset, values, executeCompletion);
    setCurrentModel(type);
  };
  const orderHandler = async result => {
    setCurrentModel(null);
    dispatch({ type: LOADING_CATERINGORDER_TRUE });
    if (result && result.error) {
      dispatch({ type: LOADING_CATERINGORDER_FALSE });
      setStripeErrorMessage(result.error.message);
    } else {
      try {
        dispatch({ type: LOADING_CATERINGORDER_TRUE });
        await updatePaymentMethodForcateringOrderFinalize(
          result,
          cateringOrder?.purchaseId,
          saveCard,
        );
        await clearCateringOrder();
        store.addNotification({
          title: 'Payment method updated!',
          message: 'Your order will now be charged using this payment method.',
          type: 'success',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: {
            duration: 4000,
          },
        });
        if (orderId) {
          getCateringOrder(+orderId);
        }
        dispatch({ type: LOADING_CATERINGORDER_FALSE });
      } catch (e) {
        dispatch({ type: LOADING_CATERINGORDER_FALSE });
        store.addNotification({
          message:
            'Something went wrong in processing your request. Please verify the details and try again.',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: {
            duration: 4000,
          },
        });
        console.error(e);
      }
    }
  };
  const onResetCart = () => {
    batch(() => {
      dispatch(resetCheckoutData());
      dispatch(removeUserCart(preset?.id));
    });
  };

  function formatTo12Hour(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);

    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return (
    <>
      <main className="pb-16 bg-gray-50/50 min-h-screen">
        {(isLoading || addressLoading) && <LoadingAnimation />}
        {cateringOrderInfo?.id && !isLoading ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
            <Formik
              {...{ initialValues, validationSchema }}
              validateOnChange
              validateOnBlur
              onSubmit={onSubmit}
            >
              {({
                setFieldValue,
                values,
                touched,
                handleBlur,
                setFieldTouched,
                resetForm,
                submitForm,
              }) => {
                const cutleryFees = values?.cutleryNeeded
                  ? standardCutleryCharges * values?.cutleryCount
                  : 0;
                const cutleryFeesInCents = values?.cutleryNeeded
                  ? standardCutleryChargesInCents * values?.cutleryCount
                  : 0;
                return (
                  <Form
                    onChange={() => setChanges(true)}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                  >
                    <UpdatePaymentModal
                      totalPrice={cateringOrderInfo?.totalPrice || 0}
                      preset={preset}
                      currentModel={currentModel}
                      setCurrentModel={type =>
                        onSubmitUpdatePayment({ type, values })
                      }
                      show={showUpdatePaymentModal}
                      setFieldValue={() => { }}
                      onClose={() => setShowUpdatePaymentModal(false)}
                      onCardClick={() => {
                        setShowUpdatePaymentModal(false);
                      }}
                      onEftClick={() => {
                        setShowUpdatePaymentModal(false);
                      }}
                    />
                    <div className="lg:col-span-8 space-y-6">
                      {(isLoading || !preset?.id) && (
                        <div className="bg-white rounded-3xl border border-gray-200/90 shadow-sm overflow-hidden">
                          <div className="h-56 sm:h-72 bg-gray-100 animate-pulse" />
                          <div className="p-5 sm:p-6 space-y-3">
                            <div className="h-6 w-1/3 bg-gray-100 rounded animate-pulse" />
                            <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      )}

                      {!isLoading && preset?.id && (
                        <div className="bg-white rounded-3xl border border-gray-200/90 shadow-sm overflow-hidden">
                          <div className="relative h-56 sm:h-72 w-full overflow-hidden bg-gray-100">
                            <img
                              className="w-full h-full object-cover"
                              src={preset.bannerImageUrl}
                              alt="banner_img"
                              referrerPolicy="no-referrer"
                              onError={e => {
                                e.target.onerror = null;
                                e.target.src = '/panda_elegant_banner.jpg';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 text-white max-w-md">
                              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight drop-shadow-sm">
                                {preset.name}
                              </h1>
                              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 bg-white/95 px-2 py-0.5 rounded-lg text-xs font-bold text-panda-dark">
                                  <Star className="w-3 h-3 fill-panda-red text-panda-red" />
                                  {preset.averageRating}
                                </span>
                                <span className="inline-flex items-center gap-1 text-white/90 text-xs sm:text-sm font-medium drop-shadow-xs">
                                  <Utensils className="w-3.5 h-3.5" />
                                  Ordered {preset.orderCount} times
                                </span>
                              </div>
                            </div>
                            {!!preset?.chefs?.length && (
                              <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 flex -space-x-3">
                                {preset.chefs.map(chef => (
                                  <div
                                    key={chef.id}
                                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white p-1 shadow-xl border-2 border-white overflow-hidden"
                                  >
                                    <img
                                      className="w-full h-full object-cover rounded-xl"
                                      src={chef.profileImageUrl}
                                      alt="chef_logo"
                                      referrerPolicy="no-referrer"
                                      onError={e => {
                                        e.target.onerror = null;
                                        e.target.src = '/panda_avatar.jpg';
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {cateringOrderInfo?.orderType === 'delivery' && (
                            <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 bg-panda-tint/20">
                              <div className="p-3.5 sm:p-5 text-center">
                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider">
                                  Delivery Fee
                                </p>
                                <p className="font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5">
                                  ${priceDecimalFormatter(deliveryFee)}
                                </p>
                              </div>
                              <div className="p-3.5 sm:p-5 text-center">
                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider">
                                  Notice Required
                                </p>
                                <p className="font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5">
                                  {preset.preOrderNoticeHour}h
                                </p>
                              </div>
                              <div className="p-3.5 sm:p-5 text-center">
                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider">
                                  Minimum Order
                                </p>
                                <p className="font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5">
                                  $
                                  {priceDecimalFormatter(
                                    preset.minimumOrderAmountCents,
                                  )}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="p-5 sm:p-6">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              <TruncateWrapper
                                text={preset.description}
                                threshold={PRESET_TRUNCATE_THRESHOLD}
                              />
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200/80 shadow-xs space-y-4">
                        <div className="border-b border-gray-100 pb-3">
                          <h2 className="text-xl sm:text-2xl font-heading font-extrabold text-[#202124]">
                            Edit Delivery Details
                          </h2>
                          <p className="text-xs text-gray-500 mt-1">
                            Your details
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="oe-field-label" htmlFor="firstName">
                              First Name
                            </label>
                            <div className="oe-input-wrap">
                              <Field
                                type="input"
                                className="form-control"
                                name="firstName"
                                id="firstName"
                              />
                            </div>
                            <FormError name="firstName" />
                          </div>
                          <div>
                            <label className="oe-field-label" htmlFor="lastName">
                              Last Name
                            </label>
                            <div className="oe-input-wrap">
                              <Field
                                type="input"
                                className="form-control"
                                name="lastName"
                                id="lastName"
                              />
                            </div>
                            <FormError name="lastName" />
                          </div>
                          <div>
                            <label className="oe-field-label" htmlFor="companyName">
                              Company Name
                            </label>
                            <div className="oe-input-wrap">
                              <Field
                                type="input"
                                className="form-control"
                                name="companyName"
                                id="companyName"
                              />
                            </div>
                            <FormError name="companyName" />
                          </div>
                          <div>
                            <label className="oe-field-label" htmlFor="phoneNumber">
                              Phone Number
                            </label>
                            <div className="oe-input-wrap">
                              <Field
                                type="input"
                                className="form-control"
                                name="phoneNumber"
                                id="phoneNumber"
                              />
                            </div>
                            <FormError name="phoneNumber" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="oe-field-label" htmlFor="emailAddress">
                              Email
                            </label>
                            <div className="oe-input-wrap">
                              <Field
                                className="form-control"
                                name="emailAddress"
                                id="emailAddress"
                                type="email"
                              />
                            </div>
                            <FormError name="emailAddress" />
                          </div>
                        </div>

                        {cateringOrderInfo?.orderType === 'delivery' && (
                          <div className="pt-4 border-t border-gray-100 space-y-4">
                            <h3 className="font-heading font-bold text-sm text-[#202124] flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-panda-red" />
                              Delivery details
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div className="sm:col-span-2">
                                <label className="oe-field-label" htmlFor="checkout-address">
                                  Delivery Address
                                </label>
                                <div className="oe-input-wrap">
                                  <AddressPickerField
                                    id="checkout-address"
                                    name="address"
                                    value={values.address}
                                    addAddressLocal={false}
                                    onSetLatLng={({ address }) => {
                                      setAddressForCoordinates(address);
                                    }}
                                    defaultAddress={
                                      cateringOrderInfo?.deliveryAddress
                                    }
                                    onChange={(name, address) => {
                                      setChanges(true);
                                      setFieldValue(name, address);
                                    }}
                                    onFocus={() => {
                                      setAddressPickerFocused(true);
                                      setFieldTouched('address');
                                    }}
                                    onBlur={e => {
                                      setAddressPickerFocused(false);
                                      handleBlur(e);
                                    }}
                                  />
                                </div>
                                <FormError name="address" />
                              </div>
                              <div>
                                <label className="oe-field-label" htmlFor="address2">
                                  Suite #
                                </label>
                                <div className="oe-input-wrap">
                                  <Field
                                    type="input"
                                    className="form-control"
                                    name="address2"
                                    id="address2"
                                  />
                                </div>
                                <FormError name="address2" />
                              </div>
                            </div>
                          </div>
                        )}

                        {cateringOrderInfo?.orderType === 'pickup' && (
                          <div className="pt-4 border-t border-gray-100 space-y-4">
                            <h3 className="font-heading font-bold text-sm text-[#202124] flex items-center gap-1.5">
                              <MapPin className="w-4 h-4 text-panda-red" />
                              Pick up details
                            </h3>
                            <div>
                              <label className="oe-field-label" htmlFor="checkout-address">
                                Pick up Address
                              </label>
                              <div className="oe-input-wrap">
                                <input
                                  type="text"
                                  readOnly
                                  id="checkout-address"
                                  className="form-control"
                                  value={restaurantAddress}
                                />
                              </div>
                              <FormError name="address" />
                            </div>
                          </div>
                        )}

                        <div className="pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="oe-datepicker-wrap">
                            <label className="oe-field-label" htmlFor="checkout-date">
                              <CalendarDays className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-panda-red" />
                              {cateringOrderInfo?.orderType === 'pickup'
                                ? 'Pick up Date'
                                : 'Delivery Date'}
                            </label>
                            <div className="oe-input-wrap">
                              <DatePickerField
                                id="checkout-date"
                                name="date"
                                value={values.date}
                                onChange={(name, val) => {
                                  setChanges(true);
                                  setFieldValue(name, val);
                                }}
                                onFocus={() => {
                                  setDatePickerFocused(true);
                                  setFieldTouched('date');
                                }}
                                onBlur={e => {
                                  setDatePickerFocused(false);
                                  handleBlur(e);
                                }}
                              />
                            </div>
                            <FormError name="date" />
                          </div>
                          <div className="oe-datepicker-wrap">
                            <label className="oe-field-label" htmlFor="checkout-time">
                              <Clock className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-panda-red" />
                              {cateringOrderInfo?.orderType === 'pickup'
                                ? 'Pick up Time'
                                : 'Delivery Time'}
                            </label>
                            <div className="oe-input-wrap">
                              <DatePickerField
                                id="checkout-time"
                                name="time"
                                showTimeSelect
                                showTimeSelectOnly
                                value={values.time}
                                onChange={(name, val) => {
                                  setChanges(true);
                                  setFieldValue(name, val);
                                }}
                                onFocus={() => {
                                  setTimePickerFocused(true);
                                  setFieldTouched('time');
                                }}
                                onBlur={e => {
                                  setTimePickerFocused(false);
                                  handleBlur(e);
                                }}
                              />
                            </div>

                            {/* Validate StoreHours */}
                            {values.date && values.time && deliveryData?.storeHours ? (
                              validateStoreHours(values.date, values.time, deliveryData.storeHours) && (
                                <p className="text-xs text-panda-red font-semibold mt-1">
                                  {validateStoreHours(values.date, values.time, deliveryData.storeHours)}
                                </p>
                              )
                            ) : null}

                            <FormError name="time" />
                            {preset?.presetLocation?.timeZone && (
                              <p className="text-[11px] text-gray-500 mt-1">
                                This is based on {preset?.presetLocation?.timeZone}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="pt-4 border-t border-gray-100">
                          <label className="oe-field-label" htmlFor="checkout-notes">
                            Additional{' '}
                            {cateringOrderInfo?.orderType === 'pickup'
                              ? 'pick up '
                              : 'delivery '}
                            instructions
                          </label>
                          <div className="oe-input-wrap">
                            <Field
                              component="textarea"
                              className="form-control"
                              name="notes"
                              id="checkout-notes"
                              rows={2}
                            />
                          </div>
                          <FormError name="notes" />
                        </div>

                        <FormError name="cutleryCount" />
                        <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex flex-wrap items-center gap-4">
                            <label className="oe-radio-pill" htmlFor="individually">
                              <Field
                                id="individually"
                                type="radio"
                                name="servingStyle"
                                value={SERVING_STYLE.INDIVIDUAL}
                              />
                              Serve Individually Packaged
                            </label>
                            <label className="oe-radio-pill" htmlFor="family">
                              <Field
                                id="family"
                                type="radio"
                                name="servingStyle"
                                value={SERVING_STYLE.FAMILY}
                              />
                              Serve Family Style
                            </label>
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="oe-checkbox-pill" htmlFor="checkout-cutlery">
                              <Field
                                id="checkout-cutlery"
                                type="checkbox"
                                name="cutleryNeeded"
                                checked={values.cutleryNeeded}
                                onChange={e => {
                                  const value = e.target.checked;
                                  if (!value && values?.cutleryCount > 0) {
                                    return;
                                  }
                                  setFieldValue('cutleryNeeded', value);
                                }}
                              />
                              {user?.isComplimentaryCutlery === true
                                ? "Add Complimentary Cutlery"
                                : "I need Cutlery/Plates/Napkins"
                              }
                            </label>
                            <Field
                              type="input"
                              className="oe-inline-number"
                              name="cutleryCount"
                              disabled={!values.cutleryNeeded}
                            />
                          </div>
                        </div>
                      </div>

                      {preset?.id && preset?.chefs && preset?.chefs.length > 0 && (
                        <div className="oe-menu-card">
                          <RestaurantMenu
                            {...{
                              chefs: preset?.chefs,
                              presetItems: checkoutData,
                              setCurrentPresetItem,
                              checkoutData,
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="lg:col-span-4">
                      <div className="lg:sticky lg:top-24 mb-4">
                        <OrderDetails
                          preset={{
                            ...preset,
                            presetItems: checkoutData,
                          }}
                          typeDetails={false}
                          buttons={
                            <div className="space-y-2.5 mt-3">
                              <button
                                type="button"
                                disabled={!changes}
                                onClick={() => handleUpdate()}
                                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark hover:from-panda-dark hover:to-panda-dark text-white font-heading font-bold text-sm shadow-md shadow-panda-red/30 active:scale-98 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                              >
                                Update Order
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (
                                    !checkoutData ||
                                    checkoutData?.length === 0
                                  ) {
                                    store.addNotification({
                                      title: 'No Items',
                                      message:
                                        'At least one item is required in your order.',
                                      type: 'danger',
                                      insert: 'top',
                                      container: 'top-right',
                                      animationIn: ['animated', 'bounceIn'],
                                      animationOut: ['animated', 'fadeOut'],
                                      dismiss: {
                                        duration: 4000,
                                      },
                                    });
                                    return;
                                  }
                                  setShowUpdatePaymentModal(true);
                                }}
                                className="w-full py-3 px-4 rounded-xl border border-gray-200 font-heading font-bold text-sm text-[#202124] hover:border-panda-red hover:text-panda-red transition-colors"
                              >
                                Update Payment Method
                              </button>
                              <button
                                type="button"
                                disabled={!changes}
                                onClick={() => handleCancel()}
                                className="w-full py-3 px-4 rounded-xl border border-gray-200 font-heading font-bold text-sm text-gray-500 hover:border-red-200 hover:text-panda-red transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Cancel Changes
                              </button>
                            </div>
                          }
                          deliveryPriceCents={deliveryFee}
                          checkoutData={checkoutData}
                          removeCheckoutItem={checkoutItemId => {
                            setRemovePresetId(checkoutItemId);
                          }}
                          setCurrentPresetItemFromCheckoutData={
                            setCurrentPresetItemFromCheckoutData
                          }
                          cutleryFeesInCents={cutleryFeesInCents}
                          cutleryNeeded={values.cutleryNeeded}
                          cutleryCount={values.cutleryCount}
                          cutleryFees={cutleryFees}
                        />
                      </div>
                    </div>
                    {cancelChanges && (
                      <ConfirmDialog
                        description="Are you sure that you want to cancel changes? All changes made to the order will be reverted back to the original order."
                        confirmText="Yes"
                        cancelText="No"
                        onConfirm={() => {
                          resetForm();
                          setCheckoutData(initialCheckout);
                          setCancelChanges(false);
                          setChanges(false);
                        }}
                        onClose={() => {
                          setCancelChanges(false);
                        }}
                      />
                    )}
                    {updateChanges && (
                      <ConfirmDialog
                        description="Are you sure that you want to submit this updated order? Once the order has been submitted it will be sent to the vendor."
                        confirmText="Yes"
                        cancelText="No"
                        onConfirm={() => {
                          submitForm();
                          setUpdateChanges(false);
                          setChanges(false);
                        }}
                        onClose={() => {
                          setUpdateChanges(false);
                        }}
                      />
                    )}
                  </Form>
                );
              }}
            </Formik>

            {removePresetId && (
              <ConfirmDialog
                description="Are you sure you want to remove this item from your order?"
                confirmText="Remove"
                onConfirm={() => {
                  removePresetItem(removePresetId);
                  setRemovePresetId(null);
                }}
                onClose={() => {
                  setRemovePresetId(null);
                }}
              />
            )}
            {currentPresetItem && (
              <PresetItemView
                presetItem={currentPresetItem}
                initialValues={
                  checkoutData.find(
                    presetForm => presetForm.id === currentPresetItem.id,
                  ) || buildCheckoutDataFromPresetItem(currentPresetItem)
                }
                chefInfo={getCurrentPresetItemChefInfo}
                onClose={() => {
                  setCurrentPresetItem(null);
                }}
                onSubmit={(presetItem, form) => {
                  setChanges(true);
                  updateCheckoutData(presetItem, form);
                }}
              />
            )}
          </div>
        ) : null}
        <section className="total-footer hide-desktop">
          <OrderBasket
            buttonWrap={
              <>
                <button
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark text-white font-heading font-bold text-sm mr-3 disabled:opacity-40"
                  type="button"
                  disabled={!changes}
                  onClick={e => {
                    e.stopPropagation();
                    handleUpdate();
                  }}
                >
                  Update
                </button>
                <button
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-heading font-bold text-sm disabled:opacity-40"
                  type="button"
                  disabled={!changes}
                  onClick={e => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                >
                  Cancel
                </button>
              </>
            }
            totalItems={checkoutData?.length}
            preset={{
              ...preset,
              presetItems: checkoutData,
            }}
            receivingMethod={cateringOrderInfo?.orderType}
            deliveryPriceCents={deliveryFee}
            checkoutData={checkoutData}
            removeCheckoutItem={checkoutItemId => {
              setRemovePresetId(checkoutItemId);
            }}
            setCurrentPresetItemFromCheckoutData={
              setCurrentPresetItemFromCheckoutData
            }
          />
        </section>
      </main>
      <StripeFormModal
        show={currentModel === 'card'}
        onClose={() => {
          setCurrentModel(null);
        }}
        orderHandler={orderHandler}
        loadingHandler={() => {
          dispatch({ type: LOADING_CATERINGORDER_TRUE });
        }}
        selectedStripePaymentMethod={
          cateringOrderInfo?.selectedStripePaymentMethod
        }
        clearCateringOrder={clearCateringOrder}
        cateringOrder={cateringOrder}
        totalPrice={cateringOrderInfo?.totalPrice || 0}
        setSaveCard={setSaveCard}
        loader={loading}
      />
      <EftFormModal
        show={currentModel === 'eft'}
        onClose={() => {
          setCurrentModel(null);
        }}
        orderHandler={orderHandler}
        loadingHandler={() => {
          dispatch({ type: LOADING_CATERINGORDER_TRUE });
        }}
        selectedStripePaymentMethod={
          cateringOrderInfo?.selectedStripePaymentMethod
        }
        clearCateringOrder={clearCateringOrder}
        cateringOrder={cateringOrder}
        totalPrice={cateringOrderInfo?.totalPrice || 0}
        setSaveCard={setSaveCard}
      />
    </>
  );
}

const mapStateToProps = state => ({
  user: state.user && state.user.user,
  cateringOrderInfo: state.caterings.cateringOrderInfo,
  cateringOrderError: state.caterings.error,
  cateringOrder: state.caterings.cateringOrder,
});
const mapDispatchToProps = {
  getCateringOrder,
  getLastMinuteAddItem,
  setPreset,
  cateringOrderUpdate,
  clearCateringOrder,
  updatePaymentMethodForcateringOrderFinalize,
};

OrderEdit.propTypes = {
  cateringOrderInfo: PropTypes.shape({
    presetId: PropTypes.number,
    deliveryFee: PropTypes.number,
    totalPrice: PropTypes.number,
    id: PropTypes.number,
    orderType: PropTypes.string,
    deliveryAddress: PropTypes.string,
    deliverySuiteAddress: PropTypes.string,
    orderDateTime: PropTypes.string,
    displayDeliveryDateTime: PropTypes.string,
    moreDetail: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    companyName: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    extraInfo: PropTypes.arrayOf({
      servingStyle: PropTypes.string,
      numCutlery: PropTypes.number,
    }),
    purchaseItems: PropTypes.arrayOf({
      menuName: PropTypes.string,
      menuPrice: PropTypes.number,
      quantity: PropTypes.number,
    }),
    chefId: PropTypes.number,
  }).isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    companyName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    confirmed: PropTypes.bool,
    address2: PropTypes.string,
    defaultDeliveryInstructions: PropTypes.string,
  }).isRequired,
  getCateringOrder: PropTypes.func.isRequired,
  createLastMinuteAddItem: PropTypes.func.isRequired,
  // cateringOrderError: PropTypes.string.isRequired,

  cateringOrderError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  setPreset: PropTypes.func.isRequired,
  cateringOrderUpdate: PropTypes.func.isRequired,
  clearCateringOrder: PropTypes.func.isRequired,
  cateringOrder: PropTypes.shape({
    saved_cards: PropTypes.arrayOf(
      PropTypes.objectOf({
        name: PropTypes.string,
        payment_method_id: PropTypes.string,
      }),
    ),
    purchaseId: PropTypes.number,
  }).isRequired,
};

const connectedHeader = connect(mapStateToProps, mapDispatchToProps)(OrderEdit);
const RoutedOrderEdit = withRouter(connectedHeader);

// Stripe.js is only loaded once this page actually mounts, not globally on
// app load - see src/index.js and components/common/WithStripe for why.
const OrderEditWithStripe = props => (
  <WithStripe>
    <RoutedOrderEdit {...props} />
  </WithStripe>
);

export default OrderEditWithStripe;
